import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import clientSiteService from '../../../services/clientSiteService';
import employeeDeploymentService from '../../../services/employeeDeploymentService';

const DeployEmployeeModal = ({ isOpen, onClose, employee, applicant, onSuccess }) => {
  const [clientSites, setClientSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    client_site_id: '',
    deployment_start_date: new Date().toISOString().split('T')[0],
    deployment_end_date: '',
    status: 'active',
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadClientSites();
      const initialStartDate = applicant?.joining_date 
        ? applicant.joining_date.split('T')[0] 
        : new Date().toISOString().split('T')[0];

      setFormData({
        client_site_id: '',
        deployment_start_date: initialStartDate,
        deployment_end_date: '',
        status: 'active',
        notes: applicant?.name || applicant?.applicant_name 
          ? `Deployed from Candidate Pipeline - ${applicant.name || applicant.applicant_name}` 
          : ''
      });
      setError(null);
    }
  }, [isOpen, applicant]);

  const loadClientSites = async () => {
    try {
      setLoadingSites(true);
      const res = await clientSiteService.getClientSites({ is_active: 'true' });
      if (res?.success) {
        setClientSites(res.data || []);
      } else {
        setError(res?.error || 'Failed to load client sites');
      }
    } catch (err) {
      console.error('Error loading client sites:', err);
      setError('Failed to load client sites');
    } finally {
      setLoadingSites(false);
    }
  };

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const employeeId = employee?.id || employee?.employee_id;
    if (!employeeId) {
      setError('Employee reference missing. Please ensure employee is created first.');
      return;
    }

    if (!formData.client_site_id) {
      setError('Client site is required');
      return;
    }

    if (!formData.deployment_start_date) {
      setError('Deployment start date is required');
      return;
    }

    if (formData.deployment_end_date && formData.deployment_end_date < formData.deployment_start_date) {
      setError('Deployment end date cannot be earlier than start date');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        employee_id: employeeId,
        client_site_id: formData.client_site_id,
        deployment_start_date: formData.deployment_start_date,
        deployment_end_date: formData.deployment_end_date || null,
        status: formData.status || 'active',
        notes: formData.notes || ''
      };

      const result = await employeeDeploymentService.createDeployment(payload);

      if (result?.success) {
        if (onSuccess) {
          onSuccess(result.data);
        }
        onClose();
      } else {
        setError(result?.error || 'Failed to create deployment record.');
      }
    } catch (err) {
      console.error('Error deploying employee:', err);
      setError('An error occurred while creating deployment record.');
    } finally {
      setLoading(false);
    }
  };

  const employeeName = employee?.full_name || employee?.name || applicant?.name || applicant?.applicant_name || 'Employee';
  const employeeCode = employee?.employee_code || employee?.employee_id || '';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-card rounded-xl border border-border w-full max-w-lg shadow-xl overflow-hidden animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-muted/20">
          <div>
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Icon name="MapPin" className="text-primary" size={22} />
              Deploy Employee
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Create a deployment record for <span className="font-semibold text-foreground">{employeeName}</span> {employeeCode ? `(${employeeCode})` : ''}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Client Site <span className="text-red-500">*</span>
            </label>
            {loadingSites ? (
              <div className="text-sm text-muted-foreground p-2">Loading client sites...</div>
            ) : (
              <select
                value={formData.client_site_id}
                onChange={(e) => handleChange('client_site_id', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary/20 outline-none"
                required
              >
                <option value="">Select Client Site</option>
                {clientSites.map(site => (
                  <option key={site.id} value={site.id}>
                    {site.site_name} {site.company_name ? `- ${site.company_name}` : ''} {site.city ? `(${site.city})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Deployment Start Date *"
              type="date"
              value={formData.deployment_start_date}
              onChange={(e) => handleChange('deployment_start_date', e.target.value)}
              required
            />

            <Input
              label="Deployment End Date"
              type="date"
              value={formData.deployment_end_date}
              onChange={(e) => handleChange('deployment_end_date', e.target.value)}
              min={formData.deployment_start_date}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Deployment Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary/20 outline-none"
            >
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="transferred">Transferred</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Deployment Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary/20 outline-none text-sm resize-none"
              rows={3}
              placeholder="Add deployment notes or specifications..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border mt-6">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? (
                <span className="flex items-center gap-2">
                  <Icon name="Loader2" className="animate-spin" size={16} />
                  Deploying...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Icon name="CheckCircle" size={16} />
                  Deploy Employee
                </span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeployEmployeeModal;
